// Typed EventSource wrapper for the search SSE stream.
// Auto-closes on terminal status events (complete | failed | partial_failure).
// The BE emits: { type, data: { status, offers, aggregator_status, error } }

export type SseEventType =
  | 'aggregator_status'
  | 'offer'
  | 'search_status'
  | 'error';

export interface SseAggregatorStatus {
  aggregator: string;
  status: 'pending' | 'searching' | 'complete' | 'failed';
  errorCode?: string;
}

export interface SseSearchStatus {
  status: 'searching' | 'complete' | 'failed' | 'partial_complete';
  message?: string;
}

export interface SseRawEvent {
  type: SseEventType;
  data: unknown;
}

export type SseHandler = (event: SseRawEvent) => void;

const TERMINAL_STATUSES = new Set(['complete', 'failed', 'partial_complete']);

/**
 * Open an SSE connection to /api/v1/searches/:searchId/stream.
 * Returns a cleanup function that closes the connection.
 */
export function openSearchStream(
  searchId: string,
  onEvent: SseHandler,
  onClose?: () => void,
): () => void {
  const url = `/api/v1/searches/${searchId}/stream`;
  const es = new EventSource(url, { withCredentials: false });

  es.onmessage = (raw) => {
    let parsed: SseRawEvent;
    try {
      parsed = JSON.parse(raw.data as string) as SseRawEvent;
    } catch {
      return;
    }

    onEvent(parsed);

    // Close on terminal status
    if (parsed.type === 'search_status') {
      const sd = parsed.data as SseSearchStatus;
      if (TERMINAL_STATUSES.has(sd.status)) {
        es.close();
        onClose?.();
      }
    }
  };

  es.onerror = () => {
    es.close();
    onClose?.();
  };

  return () => {
    es.close();
    onClose?.();
  };
}
